<template>
    <div class="relative" v-click-outside="onClickOutside">
        <span class="cursor-pointer" v-on:click="toggleMenu">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 14 14"
                class="w-5 h-5 stroke-current text-gray-500 hover:text-navy-dark"
                :class="{
                    'text-navy-dark': menuopen,
                    'text-gray-500': !menuopen,
                }"
            >
                <g>
                    <circle
                        cx="7"
                        cy="2"
                        r="1.5"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    ></circle>
                    <circle
                        cx="7"
                        cy="7"
                        r="1.5"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    ></circle>
                    <circle
                        cx="7"
                        cy="12"
                        r="1.5"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    ></circle>
                </g>
            </svg>
        </span>
        <div v-if="menuopen" class="absolute w-64 top-4 right-0 z-50 bg-white rounded-lg shadow-lg border border-gray-200">
            <!-- Tab Headers -->
            <div class="flex border-b border-gray-200">
                <button 
                    @click="activeTab = 'soil'"
                    class="flex-1 px-3 py-3 text-sm font-medium rounded-tl-lg flex items-center justify-center space-x-1"
                    :class="activeTab === 'soil' 
                        ? 'bg-green-50 text-green-700 border-b-2 border-green-600' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'"
                >
                    <svg-icon type="mdi" :path="icons.soilAnalysisReports" :size="16"></svg-icon>
                    <span>Soil Analysis</span>
                    <div class="relative">
                        <button
                            @click.stop="showSoilAnalysisInfo = !showSoilAnalysisInfo"
                            class="p-1 text-green-500 hover:text-green-700 focus:outline-none transition-colors"
                            type="button"
                            title="View soil analysis report information"
                        >
                            <svg-icon type="mdi" :path="icons.info" :size="12"></svg-icon>
                        </button>
                        
                        <!-- Info Popover -->
                        <div
                            v-if="showSoilAnalysisInfo"
                            class="absolute top-6 right-0 w-64 bg-white border border-green-200 rounded-lg shadow-lg z-50 p-3"
                            @click.stop
                        >
                            <div class="text-xs">
                                <p class="font-medium text-green-700 mb-1">Combined Soil Analysis Reports</p>
                                <p class="text-green-600">Full report with color-coded maps based on soil test results for all farms</p>
                            </div>
                            <div class="absolute top-0 right-4 transform -translate-y-1">
                                <div class="w-2 h-2 bg-white border-l border-t border-green-200 transform rotate-45"></div>
                            </div>
                        </div>
                    </div>
                </button>
                <button 
                    @click="activeTab = 'task'"
                    class="flex-1 px-3 py-3 text-sm font-medium rounded-tr-lg flex items-center justify-center space-x-1"
                    :class="activeTab === 'task' 
                        ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'"
                >
                    <svg-icon type="mdi" :path="icons.taskReports" :size="16"></svg-icon>
                    <span>Task Reports</span>
                    <div class="relative">
                        <button
                            @click.stop="showTaskReportInfo = !showTaskReportInfo"
                            class="p-1 text-blue-500 hover:text-blue-700 focus:outline-none transition-colors"
                            type="button"
                            title="View task report information"
                        >
                            <svg-icon type="mdi" :path="icons.info" :size="12"></svg-icon>
                        </button>
                        
                        <!-- Info Popover -->
                        <div
                            v-if="showTaskReportInfo"
                            class="absolute top-6 right-0 w-64 bg-white border border-blue-200 rounded-lg shadow-lg z-50 p-3"
                            @click.stop
                        >
                            <div class="text-xs">
                                <p class="font-medium text-blue-700 mb-1">Combined Task Reports</p>
                                <p class="text-blue-600">Basic report consisting of only the soil sampling map for all farms</p>
                            </div>
                            <div class="absolute top-0 right-4 transform -translate-y-1">
                                <div class="w-2 h-2 bg-white border-l border-t border-blue-200 transform rotate-45"></div>
                            </div>
                        </div>
                    </div>
                </button>
            </div>

            <!-- Tab Content -->
            <div class="p-3">
                <!-- Soil Analysis Reports Tab -->
                <div v-if="activeTab === 'soil'">
                    <!-- No Lab Results Message -->
                    <div v-if="isCombinedSoilAnalysisDisabled" class="py-6 text-center text-gray-600">
                        <div class="flex flex-col items-center space-y-3">
                            <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                            <div>
                                <p class="font-medium text-gray-700 text-base">No Lab Results Available</p>
                                <p class="text-sm text-gray-500 mt-1 max-w-64">
                                    Combined soil analysis reports require at least one sample with lab test results.
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Active State - Normal Actions -->
                    <div v-else class="space-y-1">
                        <div class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-green-50 transition-colors">
                            <a
                                v-on:click="handleCreateSoilAnalysisReport"
                                class="flex items-center space-x-3 cursor-pointer"
                                :href="CreateAnalysisReportCombine"
                            >
                                <svg-icon type="mdi" :path="icons.createReport" :size="16" class="text-green-600"></svg-icon>
                                <span v-if="analysisReportCombineEnabled">Re-create</span>
                                <span v-else>Create</span>
                            </a>
                        </div>

                        <div
                            v-if="analysisReportCombineEnabled"
                            class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-green-50 transition-colors"
                        >
                            <a
                                class="flex items-center space-x-3 cursor-pointer"
                                @click.stop="showPreviewModal('soil_analysis')"
                            >
                                <svg-icon type="mdi" :path="icons.viewReport" :size="16" class="text-green-600"></svg-icon>
                                <span>View</span>
                            </a>
                        </div>

                        <div
                            v-if="analysisReportCombineEnabled && canEmail"
                            class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-green-50 transition-colors"
                        >
                            <a class="flex items-center justify-between cursor-pointer" @click.stop="showEmailModal('soil_analysis')">
                                <div class="flex items-center space-x-3">
                                    <svg-icon type="mdi" :path="icons.emailReport" :size="16" class="text-green-600"></svg-icon>
                                    <span>Email</span>
                                </div>
                                <svg v-if="combineSoilAnalysisEmailedAt" class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>

                <!-- Task Reports Tab -->
                <div v-if="activeTab === 'task'" class="space-y-1">
                    <div class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-blue-50 transition-colors">
                        <a
                            v-on:click="handleCreateTaskReport"
                            class="flex items-center space-x-3 cursor-pointer"
                            :href="createTaskReportCombine"
                        >
                            <svg-icon type="mdi" :path="icons.createReport" :size="16" class="text-blue-600"></svg-icon>
                            <span v-if="taskReportCombineEnabled">Re-create</span>
                            <span v-else>Create Combined Task Report</span>
                        </a>
                    </div>

                    <div
                        v-if="taskReportCombineEnabled"
                        class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-blue-50 transition-colors"
                    >
                        <a
                            class="flex items-center space-x-3 cursor-pointer"
                            @click.stop="showPreviewModal('task_report')"
                        >
                            <svg-icon type="mdi" :path="icons.viewReport" :size="16" class="text-blue-600"></svg-icon>
                            <span>View</span>
                        </a>
                    </div>

                    <div
                        v-if="taskReportCombineEnabled && canEmail"
                        class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-blue-50 transition-colors"
                    >
                        <a class="flex items-center justify-between cursor-pointer" @click.stop="showEmailModal('task_report')">
                            <div class="flex items-center space-x-3">
                                <svg-icon type="mdi" :path="icons.emailReport" :size="16" class="text-blue-600"></svg-icon>
                                <span>Email</span>
                            </div>
                            <svg v-if="combineTaskReportEmailedAt" class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                            </svg>
                        </a>
                    </div>
                </div>

                <!-- Empty State Messages -->
                <div v-if="activeTab === 'task' && !createTaskReportCombine && !taskReportCombineEnabled" class="py-6 text-center text-gray-500 text-sm">
                    No task report actions available
                </div>
            </div>
        </div>

        <!-- Report Preview Modal -->
        <ReportPreviewModal
            :isOpen="previewModalOpen"
            :modalTitle="'Combined Report Preview'"
            :reportType="previewReportType === 'soil_analysis' ? 'combined_soil_analysis' : 'combined_task_report'"
            :pdfUrl="previewPdfUrl"
            :downloadUrl="previewDownloadUrl"
            :showDownload="true"
            :showEmail="canEmail"
            :showFtpUpload="canUploadToDairygold && isDairygoldTask"
            :noPreviewMessage="'Combined report preview not available'"
            :noPreviewSubMessage="'Generate the combined report first to see preview'"
            @close="closePreviewModal"
            @email-report="showEmailFromPreview"
            @ftp-upload="uploadReportFromPreview"
        />

        <!-- Email Modal Using Shared Component -->
        <ReportPreviewModal
            :isOpen="emailModalOpen"
            :modalTitle="'Email Combined Report'"
            :reportType="currentReportType === 'soil_analysis' ? 'combined_soil_analysis' : 'combined_task_report'"
            :pdfUrl="pdfUrl"
            :showDownload="false"
            :showEmail="false"
            :showFtpUpload="false"
            :noPreviewMessage="'Combined report preview not available'"
            :noPreviewSubMessage="'Generate the combined report first to see preview'"
            @close="closeEmailModal"
        >
            <template #leftPanel>
                <form @submit.prevent="sendEmail" class="h-full flex flex-col">
                    <div class="flex-1 space-y-4">
                        <h4 class="font-medium text-gray-800 mb-4">Send Email</h4>
                        
                        <div>
                            <label for="emails" class="block text-sm font-medium text-gray-700 mb-1">
                                Email Addresses * <span class="text-xs text-gray-500">(one per line or comma-separated)</span>
                            </label>
                            <textarea 
                                id="emails"
                                v-model="emailForm.emails"
                                rows="3"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter email addresses..."
                                required
                            ></textarea>
                        </div>

                        <div>
                            <label for="cc_email" class="block text-sm font-medium text-gray-700 mb-1">
                                CC Email (optional)
                            </label>
                            <input 
                                type="email" 
                                id="cc_email"
                                v-model="emailForm.cc_email"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label for="message" class="block text-sm font-medium text-gray-700 mb-1">
                                Custom Message (optional)
                            </label>
                            <textarea 
                                id="message"
                                v-model="emailForm.message"
                                rows="4"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                maxlength="1000"
                            ></textarea>
                        </div>

                        <!-- Lab Certificate Option -->
                        <div v-if="hasLabCertificatesAvailable">
                            <label class="flex items-center">
                                <input 
                                    type="checkbox" 
                                    v-model="emailForm.include_lab_certificate"
                                    class="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-offset-0 focus:ring-blue-200 focus:ring-opacity-50"
                                />
                                <span class="ml-2 text-sm font-medium text-gray-700">
                                    Include Lab Certificates (FBA Labs)
                                </span>
                            </label>
                            <p class="mt-1 text-xs text-gray-500">
                                All available lab certificates will be attached to the email
                            </p>
                        </div>
                    </div>

                    <div class="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                        <button 
                            type="button"
                            @click="closeEmailModal"
                            class="px-4 py-2 text-gray-600 hover:text-gray-800"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit"
                            :disabled="emailSending"
                            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {{ emailSending ? 'Sending...' : 'Send Email' }}
                        </button>
                    </div>
                </form>
            </template>
        </ReportPreviewModal>
    </div>
</template>

<script>
import vClickOutside from "click-outside-vue3";
import { useToast } from "vue-toastification";
import axios from "axios";
import SvgIcon from '@jamescoyle/vue-icon';
import * as icons from '../Partials/icons.js';
import ReportPreviewModal from './ReportPreviewModal.vue';

export default {
    components: {
        SvgIcon,
        ReportPreviewModal,
    },
    directives: {
        clickOutside: vClickOutside.directive,
    },
    emits: ['ftp-upload-success'],
    props: {
        DownloadTaskReportCombine: String,
        createTaskReportCombine: String,
        TaskReportCombineStatus: [Boolean, Number],
        DownloadAnalysisReportCombine: String,
        CreateAnalysisReportCombine: String,
        AnalysisReportCombineStatus: [Boolean, Number],
        HasNotStartedTest: Boolean,
        HasTestsWithNoLabResult: Boolean,
        HasUnmatchedSample: Boolean,
        canEmail: Boolean,
        taskId: String,
        combineTaskReportEmailedAt: String,
        combineSoilAnalysisEmailedAt: String,
        canUploadToDairygold: Boolean,
        isDairygoldTask: Boolean,
        availableLabCertificates: Array,
    },
    data() {
        return {
            menuopen: false,
            activeTab: 'soil',
            showTaskReportInfo: false,
            showSoilAnalysisInfo: false,
            emailModalOpen: false,
            emailSending: false,
            currentReportType: null,
            previewModalOpen: false,
            previewReportType: null,
            emailForm: {
                emails: '',
                cc_email: '',
                message: '',
                include_lab_certificate: true
            },
            pdfLoadingError: false,
            icons
        };
    },
    computed: {
        taskReportCombineEnabled() {
            return !!this.TaskReportCombineStatus;
        },
        analysisReportCombineEnabled() {
            return !!this.AnalysisReportCombineStatus;
        },
        hasLabCertificatesAvailable() {
            // Check if there are any available lab certificates
            return this.availableLabCertificates && this.availableLabCertificates.length > 0;
        },
        
        isCombinedSoilAnalysisDisabled() {
            // Disable combined soil analysis reports if there are tests without lab results
            return this.HasTestsWithNoLabResult;
        },

        pdfUrl() {
            if (!this.currentReportType || !this.taskId) {
                return null;
            }

            // Check if the combined report has been generated based on the report type
            const isReportGenerated = this.currentReportType === 'soil_analysis' 
                ? this.analysisReportCombineEnabled 
                : this.taskReportCombineEnabled;

            if (!isReportGenerated) {
                return null;
            }

            try {
                const filename = this.currentReportType === 'soil_analysis' 
                    ? 'Soil Analysis Report.pdf' 
                    : 'Task Report.pdf';
                    
                const baseUrl = route('app.task.download-combine', {
                    task: this.taskId,
                    report_type: this.currentReportType,
                    filename: filename
                });
                
                // Add inline parameter for preview mode
                return `${baseUrl}?inline=1`;
            } catch (error) {
                console.warn('Failed to generate combined PDF URL:', error);
                return null;
            }
        },

        previewPdfUrl() {
            if (!this.previewReportType || !this.taskId) {
                return null;
            }

            // Check if the combined report has been generated based on the report type
            const isReportGenerated = this.previewReportType === 'soil_analysis' 
                ? this.analysisReportCombineEnabled 
                : this.taskReportCombineEnabled;

            if (!isReportGenerated) {
                return null;
            }

            try {
                const filename = this.previewReportType === 'soil_analysis' 
                    ? 'Soil Analysis Report.pdf' 
                    : 'Task Report.pdf';
                    
                const baseUrl = route('app.task.download-combine', {
                    task: this.taskId,
                    report_type: this.previewReportType,
                    filename: filename
                });
                
                // Add inline parameter for preview mode
                return `${baseUrl}?inline=1`;
            } catch (error) {
                console.warn('Failed to generate preview PDF URL:', error);
                return null;
            }
        },

        previewDownloadUrl() {
            if (!this.previewReportType || !this.taskId) {
                return null;
            }

            try {
                const filename = this.previewReportType === 'soil_analysis' 
                    ? 'Soil Analysis Report.pdf' 
                    : 'Task Report.pdf';
                    
                return route('app.task.download-combine', {
                    task: this.taskId,
                    report_type: this.previewReportType,
                    filename: filename
                });
            } catch (error) {
                console.warn('Failed to generate download URL:', error);
                return null;
            }
        }
    },
    mounted() {
        this.$toast = useToast();
    },
    methods: {
        toggleMenu() {
            this.menuopen = !this.menuopen;
            // Always reset to default 'soil' tab when opening the menu
            if (this.menuopen) {
                this.activeTab = 'soil';
            }
        },

        showPreviewModal(reportType) {
            this.previewReportType = reportType;
            
            // Use nextTick to ensure proper timing
            this.$nextTick(() => {
                this.previewModalOpen = true;
                // Close dropdown menu after modal opens
                this.menuopen = false;
            });
        },

        closePreviewModal() {
            this.previewModalOpen = false;
            this.previewReportType = null;
        },

        showEmailFromPreview() {
            // Close preview modal and open email modal with the same report type
            this.currentReportType = this.previewReportType;
            this.previewModalOpen = false;
            this.showEmailModal(this.previewReportType);
        },

        async uploadReportFromPreview() {
            // Close preview modal and upload to FTP
            this.previewModalOpen = false;
            await this.uploadCombinedReportToDairygold(this.previewReportType);
        },

        onClickOutside(event) {
            // Don't close menu if email modal or preview modal is open
            if (this.emailModalOpen || this.previewModalOpen) {
                return;
            }
            
            this.menuopen = false;
            this.activeTab = 'soil'; // Reset to default tab
            this.showTaskReportInfo = false; // Close info popovers
            this.showSoilAnalysisInfo = false; // Close info popovers
        },

        async handleCreateTaskReport(e) {
            e.preventDefault();

            if (
                this.HasNotStartedTest ||
                this.HasUnmatchedSample
            ) {
                if (!(await this.checkCombineRequirements())) return null;
            }

            // Emit event for immediate UI update
            this.$emit('report-generation-started', 'task_report');
            
            this.$inertia.get(this.createTaskReportCombine);
            this.$toast.success("Combined report is being generated");
        },

        async handleCreateSoilAnalysisReport(e) {
            e.preventDefault();

            if (
                this.HasTestsWithNoLabResult ||
                this.HasNotStartedTest ||
                this.HasUnmatchedSample
            ) {
                if (!(await this.checkCombineRequirements())) return null;
            }

            // Emit event for immediate UI update
            this.$emit('report-generation-started', 'soil_analysis');
            
            this.$inertia.get(this.CreateAnalysisReportCombine);
            this.$toast.success("Combined report is being generated");
        },

        async checkCombineRequirements() {
            let message = [];

            this.HasNotStartedTest
                ? message.push("Some farms haven't been collected")
                : null;
            this.HasTestsWithNoLabResult
                ? message.push("One or more Samples with no lab test result. Please Check.")
                : null;
            this.HasUnmatchedSample
                ? message.push("One or more samples not matched to a parcel or zone. Please Check.")
                : null;

            const response = await this.$swal.fire({
                title: "Warning",
                html: message.join("<br>"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#01ae4c",
                cancelButtonColor: "#d33",
                confirmButtonText: "Generate",
            });

            if (!response.isConfirmed) {
                return false;
            }

            return true;
        },

        async showEmailModal(reportType) {
            this.currentReportType = reportType;
            this.emailModalOpen = true;
            
            // Fetch email suggestions
            try {
                const url = route('app.task.email-suggestions', {
                    task: this.taskId
                });
                
                const response = await axios.get(url);
                
                if (response.data.success) {
                    const suggestions = response.data.suggestions;
                    
                    // Pre-populate all relevant emails
                    if (suggestions.to_emails && suggestions.to_emails.length > 0) {
                        this.emailForm.emails = suggestions.to_emails.join('\n');
                    }
                    
                    // Set sender email as CC
                    if (suggestions.sender_email) {
                        this.emailForm.cc_email = suggestions.sender_email;
                    }
                }
            } catch (error) {
                console.warn('Failed to fetch email suggestions:', error);
                // Continue opening modal even if suggestions fail
            }
        },

        closeEmailModal() {
            this.emailModalOpen = false;
            this.currentReportType = null;
            this.emailForm = {
                emails: '',
                cc_email: '',
                message: '',
                include_lab_certificate: true
            };
            // Close the dropdown menu as well
            this.menuopen = false;
        },

        async sendEmail() {
            if (!this.emailForm.emails.trim()) {
                this.$toast.error('Please enter at least one email address');
                return;
            }

            // Parse emails from textarea (support both newlines and commas)
            const emails = this.emailForm.emails
                .split(/[\n,]+/)
                .map(email => email.trim())
                .filter(email => email.length > 0);

            if (emails.length === 0) {
                this.$toast.error('Please enter valid email addresses');
                return;
            }

            this.emailSending = true;

            try {
                const response = await axios.post(route('app.task.email-combined-report', {
                    task: this.taskId,
                    report_type: this.currentReportType
                }), {
                    emails: emails,
                    cc_email: this.emailForm.cc_email,
                    message: this.emailForm.message,
                    include_lab_certificate: this.emailForm.include_lab_certificate
                });

                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.closeEmailModal();
                } else {
                    this.$toast.error(response.data.message || 'Failed to send email');
                }
            } catch (error) {
                console.error('Email error:', error);
                const message = error.response?.data?.message || 'Failed to send email';
                this.$toast.error(message);
            } finally {
                this.emailSending = false;
            }
        },

        async uploadCombinedReportToDairygold(reportType) {
            if (!this.canUploadToDairygold || !this.isDairygoldTask) {
                this.$toast.error('You are not authorized to upload reports to Dairygold FTP');
                return;
            }

            try {
                this.$toast.info('Uploading combined report to Dairygold FTP...');
                
                const url = route('app.task.upload-combined-report-to-dairygold', {
                    task: this.taskId,
                    report_type: reportType
                });

                const response = await axios.post(url);

                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.menuopen = false;
                    // Emit event to refresh data in parent component
                    this.$emit('ftp-upload-success');
                    // Also directly call parent refresh method with small delay
                    setTimeout(() => {
                        if (this.$parent && this.$parent.refreshReportData) {
                            this.$parent.refreshReportData();
                        }
                    }, 200);
                } else {
                    this.$toast.error(response.data.message || 'Failed to upload combined report');
                }
            } catch (error) {
                console.error('FTP upload error:', error);
                const message = error.response?.data?.message || 'Failed to upload combined report to FTP';
                this.$toast.error(message);
            }
        },
    },
};
</script>
